import { motion } from "framer-motion";
import {
  Search,
  Target,
  MessageSquareShare,
  PenTool,
  Palette,
  BarChart3,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: Search,
    title: "SEO & Growth",
    kicker: "01",
    desc: "Programmatic SEO, technical audits, and content engines that compound month over month.",
    span: "md:col-span-8",
    tall: true,
  },
  {
    icon: Target,
    title: "Paid Ads",
    kicker: "02",
    desc: "Meta, Google, TikTok. ROAS-obsessed.",
    span: "md:col-span-4",
  },
  {
    icon: MessageSquareShare,
    title: "Social Media",
    kicker: "03",
    desc: "Community, calendars & content that actually sounds like you.",
    span: "md:col-span-4",
  },
  {
    icon: PenTool,
    title: "Content Studio",
    kicker: "04",
    desc: "In-house writers, editors, and video ops turning strategy into stories.",
    span: "md:col-span-4",
  },
  {
    icon: Palette,
    title: "Branding & Identity",
    kicker: "05",
    desc: "Names, marks, guidelines, and websites that make you unmistakable.",
    span: "md:col-span-4",
  },
  {
    icon: BarChart3,
    title: "Analytics & CRO",
    kicker: "06",
    desc: "GA4, dashboards, experiments — you'll never guess again.",
    span: "md:col-span-8",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 lg:py-40 border-b border-zinc-200"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="md:col-span-4">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">
              [ 02 — SERVICES ]
            </div>
          </div>
          <div className="md:col-span-8">
            <h2
              data-testid="services-title"
              className="font-display font-black uppercase tracking-tighter leading-none text-5xl md:text-6xl lg:text-7xl"
            >
              A full-service <br />
              <span className="text-[#002FA7]">growth stack</span>
              <br />— without the agency bloat.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {services.map((s, i) => (
            <motion.a
              href="#contact"
              key={s.title}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              data-testid={`service-card-${i}`}
              className={`group relative bg-white border border-zinc-200 p-8 md:p-10 hover:-translate-y-1 hover:border-[#002FA7] hover:shadow-[8px_8px_0_0_#002FA7] transition-all duration-300 flex flex-col ${
                s.span
              } ${s.tall ? "md:min-h-[340px]" : "md:min-h-[240px]"}`}
            >
              <div className="flex items-start justify-between mb-8">
                <s.icon
                  className="w-8 h-8 text-zinc-950 group-hover:text-[#002FA7] transition-colors"
                  strokeWidth={1.5}
                />
                <span className="font-mono text-xs tracking-widest text-zinc-400">
                  {s.kicker}
                </span>
              </div>
              <h3 className="font-display font-bold uppercase text-2xl md:text-3xl tracking-tight mb-3">
                {s.title}
              </h3>
              <p className="text-zinc-600 leading-relaxed text-sm md:text-base max-w-md">
                {s.desc}
              </p>
              <div className="mt-auto pt-8 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-950 group-hover:text-[#002FA7] transition-colors">
                Explore <ArrowUpRight className="w-4 h-4" />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
